/**
 * Kết nối lại nick Zalo ngay trong màn Hội thoại.
 *
 * Trước đây nick rớt phiên chỉ có chữ "Chưa kết nối" trong menu tài khoản,
 * còn muốn nối lại phải sang Cài đặt → Tích hợp. Nhân viên đang trả lời khách
 * thì không nhìn thấy gì, gõ xong bấm gửi mới biết tin không đi. Ở đây:
 *  - `ThanhMatKetNoi`: dải cảnh báo ngay trên ô soạn tin của hội thoại đang mở,
 *    kèm nút "Kết nối lại" (phiên đã lưu) và "Đăng nhập QR".
 *  - `NutKetNoiLai`: nút nhỏ thay cho nhãn "Chưa kết nối" trong menu tài khoản.
 *  - `useTrangThaiNick`: đọc trạng thái sống của nick từ danh sách tài khoản
 *    (cùng query với thanh lọc nên không tốn thêm request) và cập nhật theo
 *    sự kiện socket `zalo:account-status` / `zalo:connected`.
 */
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Loader2, LogIn, RefreshCw, Unplug } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { apiError } from '@/lib/api-client'
import { getSocket } from '@/lib/socket'
import { cn } from '@/lib/utils'
import {
  statusMeta,
  useZaloAccounts,
  useZaloLogin,
  useZaloReconnect,
  type ChannelAccount,
} from '@/hooks/use-integrations'
import { QrLoginDialog } from '@/pages/integrations/qr-login-dialog'

const ZALO_CA_NHAN = 2

/** Nick còn nhắn được không — theo trạng thái sống từ pool, không phải cột `status` cũ. */
export function nickDangSong(acc: Pick<ChannelAccount, 'liveStatus' | 'status' | 'isDisabled'>): boolean {
  return !acc.isDisabled && (acc.liveStatus ?? acc.status) === 'connected'
}

/**
 * Cập nhật danh sách tài khoản khi backend báo đổi trạng thái. Gắn một lần ở
 * nơi cần biết (banner), không gắn toàn cục để trang khác không refetch thừa.
 */
function useDongBoTrangThaiNick() {
  const qc = useQueryClient()
  useEffect(() => {
    const socket = getSocket()
    const lamMoi = () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] })
    socket.on('zalo:account-status', lamMoi)
    socket.on('zalo:connected', lamMoi)
    socket.on('zalo:error', lamMoi)
    return () => {
      socket.off('zalo:account-status', lamMoi)
      socket.off('zalo:connected', lamMoi)
      socket.off('zalo:error', lamMoi)
    }
  }, [qc])
}

/** Tìm nick theo id trong danh sách (kể cả nick đang tạm dừng). */
export function useTrangThaiNick(accountId: string | null | undefined) {
  const { data = [] } = useZaloAccounts(undefined, false, true)
  return accountId ? data.find((a) => a.id === accountId) ?? null : null
}

/** Hai hành động nối lại nick Zalo cá nhân + hộp QR dùng chung. */
function useHanhDongKetNoi() {
  const reconnect = useZaloReconnect()
  const login = useZaloLogin()
  const [qrId, setQrId] = useState<string | null>(null)
  const [qrOpen, setQrOpen] = useState(false)

  const ketNoiLai = (acc: ChannelAccount) =>
    reconnect.mutate(acc.id, {
      onSuccess: () => toast.success('Đang kết nối lại bằng phiên đã lưu…'),
      onError: (e) => {
        // Không còn phiên → chỉ còn cách quét QR; mở luôn cho khỏi bấm hai lần.
        const msg = apiError(e)
        if (/no saved session|QR/i.test(msg)) {
          toast.info('Phiên cũ không còn — cần quét QR đăng nhập lại')
          dangNhapQr(acc)
        } else toast.error(msg)
      },
    })

  const dangNhapQr = (acc: ChannelAccount) =>
    login.mutate(acc.id, {
      onSuccess: () => { setQrId(acc.id); setQrOpen(true) },
      onError: (e) => toast.error(apiError(e)),
    })

  const hopQr = <QrLoginDialog accountId={qrId} open={qrOpen} onOpenChange={setQrOpen} />
  return { ketNoiLai, dangNhapQr, dangBan: reconnect.isPending || login.isPending, hopQr }
}

/**
 * Dải cảnh báo trên ô soạn tin. Không hiện gì khi nick còn sống hoặc chưa tải
 * xong danh sách — thà trễ một nhịp còn hơn nháy đỏ sai.
 */
export function ThanhMatKetNoi({ accountId }: { accountId: string | null | undefined }) {
  useDongBoTrangThaiNick()
  const acc = useTrangThaiNick(accountId)
  const hd = useHanhDongKetNoi()
  if (!acc || nickDangSong(acc)) return null

  const meta = statusMeta(acc.liveStatus, acc.isDisabled)
  const dangNoi = acc.liveStatus === 'connecting' || acc.liveStatus === 'qr_pending'
  const ten = acc.displayName ?? 'Nick này'

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs"
    >
      {dangNoi
        ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-warning" />
        : <Unplug className="h-4 w-4 shrink-0 text-warning" />}
      <div className="min-w-0 flex-1">
        <span className="font-medium">{ten}</span>
        <span className="text-muted-foreground"> · {meta.label}</span>
        <span className="block text-muted-foreground">
          {dangNoi
            ? 'Đang nối lại, chờ vài giây…'
            : acc.isDisabled
              ? 'Nick đang tạm dừng — tin gửi đi sẽ không tới khách.'
              : 'Nick mất kết nối — tin gửi đi sẽ không tới khách và tin khách nhắn cũng không về.'}
        </span>
      </div>
      {acc.platform === ZALO_CA_NHAN ? (
        <div className="flex shrink-0 items-center gap-1.5">
          <Button size="sm" className="h-7" onClick={() => hd.ketNoiLai(acc)} disabled={hd.dangBan || dangNoi} title="Nối lại bằng phiên đã lưu, không cần quét QR">
            <RefreshCw className={cn('h-3.5 w-3.5', hd.dangBan && 'animate-spin')} /> Kết nối lại
          </Button>
          <Button size="sm" variant="outline" className="h-7" onClick={() => hd.dangNhapQr(acc)} disabled={hd.dangBan}>
            <LogIn className="h-3.5 w-3.5" /> Đăng nhập QR
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" className="h-7" asChild>
          <Link to="/integrations">Mở Tích hợp</Link>
        </Button>
      )}
      {hd.hopQr}
    </div>
  )
}

/**
 * Nút gọn trong menu chọn tài khoản. Nằm trong `DropdownMenuItem` nên phải chặn
 * sự kiện, nếu không bấm nút lại thành chọn lọc theo nick đó.
 */
export function NutKetNoiLai({ acc }: { acc: ChannelAccount }) {
  const hd = useHanhDongKetNoi()
  if (acc.platform !== ZALO_CA_NHAN) return null
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-6 shrink-0 px-2 text-[10.5px]"
        disabled={hd.dangBan}
        title="Nối lại bằng phiên đã lưu"
        // Radix chọn mục ở pointerup rồi đóng menu trước khi click tới nút —
        // chặn cả pointerdown/pointerup, còn hành động gắn vào click.
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); hd.ketNoiLai(acc) }}
      >
        <RefreshCw className={cn('h-3 w-3', hd.dangBan && 'animate-spin')} /> Kết nối lại
      </Button>
      {hd.hopQr}
    </>
  )
}
